/**
 * Pyodide 런타임과 Python 자산을 같은 origin 에서 받아 가상 FS 에 올린다.
 *
 * 여기서 실패하면(HTTP 오류·해시 불일치·import 실패) 연결 버튼을 켜지 않는다.
 * 배포본의 manifest 와 코드가 어긋난 채로 장치에 연결하지 않기 위해서다.
 */
import type { PyodideInterface } from "pyodide";

import { PYODIDE_BASE_URL, PYODIDE_VERSION } from "./pyodide-version";

const PYTHON_BASE_URL = "/python/";
const APP_DIR = "/app";

export interface PythonManifestEntry {
  path: string;
  bytes: number;
  sha256: string;
}

export interface PythonManifest {
  generated_by: string;
  generated_at: string;
  source_repo: string;
  source_git_head: string | null;
  pyodide_version: string;
  package: string;
  entry: string;
  files: PythonManifestEntry[];
}

export interface LoadedRuntime {
  pyodide: PyodideInterface;
  manifest: PythonManifest;
  pyodideVersion: string;
  /** web_bridge 모듈 프록시 */
  bridgeModule: any;
}

export interface LoadProgress {
  (step: string, done: number, total: number): void;
}

export class RuntimeError extends Error {
  readonly hint: string;
  constructor(message: string, hint: string) {
    super(message);
    this.name = "RuntimeError";
    this.hint = hint;
  }
}

async function fetchBytes(url: string): Promise<Uint8Array> {
  const response = await fetch(url, { cache: "no-cache" });
  if (!response.ok) {
    throw new RuntimeError(
      `자산을 받지 못했습니다: ${url} (HTTP ${response.status})`,
      "배포본이 완전한지, 경로와 캐시가 섞이지 않았는지 확인하세요.",
    );
  }
  return new Uint8Array(await response.arrayBuffer());
}

async function sha256Hex(data: Uint8Array): Promise<string> {
  if (!globalThis.crypto?.subtle) {
    throw new RuntimeError(
      "이 브라우저에서 해시를 계산할 수 없습니다.",
      "HTTPS 또는 localhost 로 열어야 합니다.",
    );
  }
  // ArrayBufferView 를 그대로 넘긴다 — 복사 없이 SubtleCrypto 가 읽는다.
  const digest = await crypto.subtle.digest("SHA-256", data as unknown as BufferSource);
  return Array.from(new Uint8Array(digest))
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");
}

function parentDirectories(path: string): string[] {
  const parts = path.split("/");
  parts.pop();
  const out: string[] = [];
  let current = APP_DIR;
  for (const part of parts) {
    current += `/${part}`;
    out.push(current);
  }
  return out;
}

/**
 * 앱 시작 때 한 번 부른다. 통신을 붙이기 전에 이 Promise 가 끝나야 한다.
 */
export async function loadRuntime(onProgress?: LoadProgress): Promise<LoadedRuntime> {
  const report = onProgress ?? (() => {});

  report("Python 자산 목록 확인", 0, 1);
  const manifestResponse = await fetch(`${PYTHON_BASE_URL}manifest.json`, { cache: "no-cache" });
  if (!manifestResponse.ok) {
    throw new RuntimeError(
      `Python 자산 목록을 받지 못했습니다 (HTTP ${manifestResponse.status}).`,
      "sync_protocol.py 로 public/python 을 만들었는지 확인하세요.",
    );
  }
  const manifest = (await manifestResponse.json()) as PythonManifest;

  if (manifest.pyodide_version !== PYODIDE_VERSION) {
    throw new RuntimeError(
      `배포 버전이 섞였습니다: manifest 는 Pyodide ${manifest.pyodide_version}, ` +
        `앱은 ${PYODIDE_VERSION} 을 씁니다.`,
      "prepare:pyodide 와 sync_protocol.py 를 같은 버전으로 다시 실행하고 배포하세요.",
    );
  }

  const total = manifest.files.length + 2;
  let done = 0;

  // 파일을 먼저 모두 받고 해시를 확인한다 — 하나라도 틀리면 런타임을 띄우지 않는다.
  const contents = new Map<string, Uint8Array>();
  for (const entry of manifest.files) {
    report(`Python 파일 확인 ${entry.path}`, done, total);
    const bytes = await fetchBytes(`${PYTHON_BASE_URL}${entry.path}`);
    if (bytes.length !== entry.bytes) {
      throw new RuntimeError(
        `Python 파일 크기가 다릅니다: ${entry.path}`,
        "배포본과 manifest 가 다른 버전입니다. 다시 배포하세요.",
      );
    }
    const hash = await sha256Hex(bytes);
    if (hash !== entry.sha256) {
      throw new RuntimeError(
        `Python 파일 해시가 다릅니다: ${entry.path}`,
        "배포본과 manifest 가 다른 버전입니다. 다시 배포하세요.",
      );
    }
    contents.set(entry.path, bytes);
    done += 1;
  }

  report("Pyodide 런타임 시작", done, total);
  // 번들러가 자산을 건드리지 않도록 같은 origin 의 고정 경로에서 직접 import 한다.
  const runtimeUrl = new URL(`${PYODIDE_BASE_URL}pyodide.mjs`, location.origin).href;
  let loadPyodide: (options: { indexURL: string }) => Promise<PyodideInterface>;
  try {
    ({ loadPyodide } = (await import(/* @vite-ignore */ runtimeUrl)) as {
      loadPyodide: (options: { indexURL: string }) => Promise<PyodideInterface>;
    });
  } catch (error) {
    throw new RuntimeError(
      `Pyodide 모듈을 불러오지 못했습니다: ${String(error)}`,
      "npm run prepare:pyodide 로 런타임 자산을 준비했는지 확인하세요.",
    );
  }

  let pyodide: PyodideInterface;
  try {
    pyodide = await loadPyodide({ indexURL: PYODIDE_BASE_URL });
  } catch (error) {
    throw new RuntimeError(
      `Pyodide 를 시작하지 못했습니다: ${String(error)}`,
      "서버가 .wasm 을 application/wasm 으로 제공하는지 확인하세요.",
    );
  }
  done += 1;

  report("Python 모듈 적재", done, total);
  // 최상위 파일(web_bridge.py 등)이 먼저 와도 되도록 뿌리를 먼저 만든다.
  pyodide.FS.mkdirTree(APP_DIR);
  const seen = new Set<string>([APP_DIR]);
  for (const [path, bytes] of contents) {
    for (const dir of parentDirectories(path)) {
      if (!seen.has(dir)) {
        pyodide.FS.mkdirTree(dir);
        seen.add(dir);
      }
    }
    pyodide.FS.writeFile(`${APP_DIR}/${path}`, bytes);
  }

  let bridgeModule: any;
  try {
    pyodide.runPython(`import sys\nif ${JSON.stringify(APP_DIR)} not in sys.path: sys.path.insert(0, ${JSON.stringify(APP_DIR)})`);
    bridgeModule = pyodide.pyimport("web_bridge");
  } catch (error) {
    throw new RuntimeError(
      `Python 모듈을 불러오지 못했습니다: ${String(error)}`,
      "public/python 자산이 이 앱 버전과 맞는지 확인하세요.",
    );
  }
  done += 1;
  report("준비 완료", done, total);

  return { pyodide, manifest, pyodideVersion: PYODIDE_VERSION, bridgeModule };
}
